import { Link } from "@/lib/router-compat";
import { ArrowRight, MapPin, CheckCircle, Star } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnswerBlock from "@/components/AnswerBlock";
import AnimatedSection from "@/components/AnimatedSection";
import MagneticButton from "@/components/MagneticButton";
import Testimonials from "@/components/Testimonials";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const zones = [
  "Lyon 1er – 9ème",
  "Grézieu-la-Varenne",
  "Craponne",
  "Tassin-la-Demi-Lune",
  "Écully",
  "Francheville",
  "Sainte-Foy-lès-Lyon",
  "Oullins",
  "Brignais",
  "Chaponost",
  "Saint-Genis-Laval",
  "Vaugneray",
  "L'Arbresle",
  "Villefranche-sur-Saône",
  "Bourgoin-Jallieu",
  "Vienne",
  "Annecy",
  "Chambéry",
  "Grenoble",
  "Saint-Étienne",
];

const expertises = [
  { title: "Aménagement d'intérieur", desc: "Plans cotés, distribution, rangements. Le document que vos artisans utiliseront sur le chantier." },
  { title: "Images 3D", desc: "La pièce vue avant travaux, pour trancher entre deux options plutôt que d'imaginer." },
  { title: "Décoration & colorimétrie", desc: "Teintes testées sur vos murs, matières, mobilier. Master en couleur à l'appui." },
  { title: "Suivi de chantier", desc: "Consultation des artisans, lecture des devis, visites, réception avec liste de réserves." },
  { title: "Fresques & art mural", desc: "Une fresque peinte sur mesure quand un mur mérite mieux qu'un cadre acheté en série." },
  { title: "Dossiers administratifs", desc: "Déclaration préalable, permis de construire, pièces graphiques pour la mairie." },
];

interface FeaturedProject {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  location: string | null;
}

const ArchitecteInterieurLyon = () => {
  const [projects, setProjects] = useState<FeaturedProject[]>([]);

  useEffect(() => {
    supabase
      .from("projects")
      .select("id, title, slug, cover_image, location")
      .eq("published", true)
      .order("display_order")
      .limit(6)
      .then(({ data }) => setProjects(data ?? []));
  }, []);

  const lyonQA = [
    {
      question: "Combien coûte un architecte d'intérieur à Lyon ?",
      answer:
        "Chez JIIA Studio, un projet complet démarre à 1 500€ et un conseil couleurs à 300€. Le montant dépend ensuite de la surface, du nombre de pièces et du niveau de suivi de chantier retenu. Le premier rendez-vous reste offert.",
    },
    {
      question: "Quelles communes sont couvertes autour de Lyon ?",
      answer:
        "Lyon et ses arrondissements, l'Ouest lyonnais (Grézieu-la-Varenne, Tassin-la-Demi-Lune, Écully, Charbonnières-les-Bains, Sainte-Foy-lès-Lyon, Brindas, Oullins, Caluire-et-Cuire) et plus largement la région Rhône-Alpes.",
    },
    {
      question: "Quel est le délai avant de voir les premiers plans ?",
      answer:
        "Le rendez-vous de relevé se cale sous quelques jours, puis les plans cotés et les rendus 3D arrivent en général sous 3 à 5 semaines selon l'ampleur du projet.",
    },
    {
      question: "Travaillez-vous aussi pour des locaux professionnels ?",
      answer:
        "Oui : commerces, restaurants, hôtels, bureaux, cabinets médicaux et bien-être, y compris sur les contraintes d'accessibilité PMR.",
    },
  ];

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "JIIA Studio – Architecte d'intérieur Lyon",
      "description": "Cabinet d'architecture d'intérieur à Lyon Ouest. Aménagement, décoration, images 3D, suivi de travaux pour particuliers et professionnels en Rhône-Alpes.",
      "url": "https://jiia-studio.com/architecte-interieur-lyon",
      "telephone": "",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Grézieu-la-Varenne",
        "addressRegion": "Auvergne-Rhône-Alpes",
        "postalCode": "69610",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 45.7385,
        "longitude": 4.6917
      },
      "areaServed": [
        { "@type": "City", "name": "Lyon" },
        { "@type": "AdministrativeArea", "name": "Rhône-Alpes" }
      ],
      "priceRange": "€€",
      "openingHoursSpecification": {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "18:00"
      },
      "founder": { "@type": "Person", "name": "Charline Jacob" }
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Architecture d'intérieur Lyon et Rhône-Alpes",
      "provider": { "@type": "LocalBusiness", "name": "JIIA Studio" },
      "areaServed": "Lyon et région Rhône-Alpes",
      "serviceType": "Architecture d'intérieur",
      "description": "Aménagement, décoration, images 3D, suivi de travaux pour particuliers et professionnels."
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": lyonQA.map((qa) => ({
        "@type": "Question",
        "name": qa.question,
        "acceptedAnswer": { "@type": "Answer", "text": qa.answer },
      })),
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Architecte d'intérieur Lyon & Rhône-Alpes | JIIA Studio"
        description="JIIA Studio, architecte d'intérieur à Lyon Ouest (Grézieu-la-Varenne). Aménagement, décoration, images 3D, suivi de travaux. Devis gratuit pour particuliers et professionnels."
        canonical="/architecte-interieur-lyon"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body mb-4">
              <MapPin className="h-4 w-4" />
              <span>Lyon · Ouest Lyonnais · Rhône-Alpes</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-light leading-tight mb-6">
              Architecte d'intérieur<br />
              <span className="italic">à Lyon & en Rhône-Alpes</span>
            </h1>
            <p className="text-lg text-muted-foreground font-body max-w-2xl leading-relaxed mb-8">
              Mon atelier est à Grézieu-la-Varenne, à vingt minutes de Lyon, ce qui me permet de passer
              sur les chantiers de l'agglomération sans y consacrer une demi-journée. Je travaille pour
              des logements et pour des locaux professionnels, du premier croquis jusqu'à la réception
              des travaux.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-sm font-body text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors">
                    Devis gratuit <ArrowRight className="h-4 w-4" />
                  </span>
                </MagneticButton>
              </Link>
              <Link to="/projets">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 border border-border rounded-sm font-body text-sm uppercase tracking-widest hover:bg-secondary transition-colors">
                    Voir mes réalisations
                  </span>
                </MagneticButton>
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Expertises */}
      <div className="pb-4">
        <AnswerBlock
          summary={"JIIA Studio est un cabinet d'architecture d'intérieur basé à Grézieu-la-Varenne, à vingt minutes de Lyon, qui accompagne particuliers et professionnels de la conception au suivi de chantier.\nUn projet complet démarre à 1 500€, un conseil couleurs à 300€ et le premier rendez-vous est offert."}
          facts={[
            "Zone : Lyon, Ouest lyonnais et Rhône-Alpes",
            "Projet complet à partir de 1 500€",
            "Conseil couleurs à partir de 300€",
            "Plans cotés et rendus 3D avant travaux",
            "Premier rendez-vous offert, réponse sous 48 h",
            "Particuliers et locaux professionnels",
          ]}
          questions={lyonQA}
        />
      </div>

      <section className="py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Expertises</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              Mes prestations d'architecture d'intérieur
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {expertises.map((exp, i) => (
              <AnimatedSection key={exp.title} delay={i * 0.1}>
                <div className="p-6 border border-border rounded-sm hover:border-accent/50 transition-colors h-full">
                  <CheckCircle className="h-5 w-5 text-accent mb-3" />
                  <h3 className="font-heading text-lg mb-2">{exp.title}</h3>
                  <p className="text-sm text-muted-foreground font-body leading-relaxed">{exp.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/prestations" className="inline-flex items-center gap-2 text-sm font-body text-accent hover:text-accent/80 transition-colors uppercase tracking-widest">
              Découvrir toutes les prestations <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Projets locaux */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28 bg-secondary/30">
          <div className="container">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Réalisations</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
                Projets d'architecture d'intérieur en Rhône-Alpes
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, i) => (
                <AnimatedSection key={project.id} delay={i * 0.1}>
                  <Link to={`/projets/${project.slug}`} className="group block">
                    <div className="aspect-[4/3] overflow-hidden rounded-sm mb-3">
                      <img
                        src={project.cover_image || ""}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{project.title}</h3>
                    {project.location && (
                      <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" /> {project.location}
                      </p>
                    )}
                  </Link>
                </AnimatedSection>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/projets" className="inline-flex items-center gap-2 text-sm font-body text-accent hover:text-accent/80 transition-colors uppercase tracking-widest">
                Voir tous les projets <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Zone d'intervention */}
      <section className="py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Zone d'intervention</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Architecte d'intérieur dans toute la région Rhône-Alpes
            </h2>
            <p className="text-muted-foreground font-body max-w-2xl leading-relaxed mb-10">
              Je me déplace dans Lyon et sa périphérie sans supplément et plus loin en Rhône-Alpes selon
              le projet. Pour un chantier à Grenoble ou à Annecy, on cale simplement les visites sur les
              étapes clés, le reste se traite en visio.
            </p>
          </AnimatedSection>
          <div className="flex flex-wrap gap-2">
            {zones.map((zone, i) => (
              <AnimatedSection key={zone} delay={i * 0.02}>
                <span className="inline-block px-4 py-2 bg-secondary text-sm font-body rounded-sm">
                  {zone}
                </span>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <Testimonials />

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <AnimatedSection>
            <Star className="h-8 w-8 mx-auto mb-4 text-rose" />
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Parlons de votre projet
            </h2>
            <p className="text-primary-foreground/70 font-body max-w-lg mx-auto mb-8 leading-relaxed">
              Décrivez-moi les lieux en quelques lignes. Premier rendez-vous offert, sans engagement, réponse sous 48h.
            </p>
            <Link to="/contact">
              <MagneticButton>
                <span className="inline-flex items-center gap-2 px-10 py-4 bg-rose text-white rounded-sm font-body text-sm uppercase tracking-widest hover:bg-rose/90 transition-colors">
                  Demander un devis gratuit <ArrowRight className="h-4 w-4" />
                </span>
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default ArchitecteInterieurLyon;
